import React, { useState, useEffect, useRef } from "react";

const SECTION_ITEMS = [
  { section: "profile",        label: "About Me",       planet: "The Sun",  color: "#FFB3D9", name: "Sun"     },
  { section: "experience",     label: "Experience",     planet: "Mercury",  color: "#E8906A", name: "Mercury" },
  { section: "projects",       label: "Projects",       planet: "Moon",     color: "#C0C0D8", name: "Moon"    },
  { section: "skills",         label: "Skills",         planet: "Venus",    color: "#E8C878", name: "Venus"   },
  { section: "education",      label: "Education",      planet: "Mars",     color: "#D4A880", name: "Mars"    },
  { section: "contact",        label: "Contact",        planet: "Earth",    color: "#78C8E8", name: "Earth"   },
  { section: "certifications", label: "Certifications", planet: "Jupiter",  color: "#C8A8F0", name: "Jupiter" },
  { section: "associative",    label: "Associations",   planet: "Saturn",   color: "#E8D890", name: "Saturn"  },
  { section: "testimonials",   label: "Testimonials",   planet: "Uranus",   color: "#90D8D8", name: "Uranus"  },
  { section: "interests",      label: "Interests",      planet: "Neptune",  color: "#8090E8", name: "Neptune" },
];

const SectionMenu = ({ onSelect, isOpen, onToggle, activeSection }) => {
  const [hovered, setHovered] = useState(null);
  const menuRef = useRef();

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onToggle();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onToggle]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onToggle(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onToggle]);

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      {/* Trigger button */}
      <button
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: isOpen ? "rgba(255,179,217,0.1)" : "none",
          border: "none",
          color: isOpen ? "#FFB3D9" : "rgba(255,235,250,0.5)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.78rem",
          fontWeight: 500,
          cursor: "none",
          padding: "4px 2px",
          transition: "color 0.2s",
          letterSpacing: "0.03em",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: "0.7rem", opacity: 0.8 }}>⊕</span>
        Explore
        <span style={{
          fontSize: "0.6rem",
          opacity: 0.6,
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
          display: "inline-block",
        }}>▲</span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div style={{
          position: "absolute",
          bottom: "calc(100% + 14px)",
          left: "50%",
          transform: "translateX(-50%)",
          width: 260,
          background: "rgba(5, 5, 8, 0.95)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          border: "1px solid rgba(255, 179, 217, 0.1)",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
          animation: "gentle-fade 0.18s ease forwards",
          zIndex: 200,
        }}>
          {/* Header */}
          <div style={{
            padding: "12px 16px 8px",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
          }}>
            <p style={{
              fontFamily: "'Cinzel', serif",
              fontSize: "0.55rem",
              letterSpacing: "0.18em",
              color: "rgba(255,235,250,0.3)",
              textTransform: "uppercase",
            }}>
              Navigate to
            </p>
          </div>

          {/* Section rows */}
          {SECTION_ITEMS.map((item, i) => {
            const isActive = item.section === activeSection;
            return (
              <button
                key={item.section}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => {
                  onToggle();
                  onSelect(item.section, item.name);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: "10px 16px",
                  paddingLeft: 13,
                  background: hovered === i
                    ? `rgba(${hexToRgb(item.color)}, 0.08)`
                    : isActive
                      ? `rgba(${hexToRgb(item.color)}, 0.05)`
                      : "transparent",
                  border: "none",
                  borderLeft: isActive ? `3px solid ${item.color}` : "3px solid transparent",
                  cursor: "none",
                  transition: "background 0.15s, border-color 0.15s",
                  gap: 10,
                }}
              >
                {/* Color dot */}
                <span style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: item.color,
                  flexShrink: 0,
                  boxShadow: (hovered === i || isActive) ? `0 0 6px ${item.color}` : "none",
                  transition: "box-shadow 0.15s",
                }} />

                {/* Section name */}
                <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.85rem",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "#FFF0F7" : hovered === i ? "#FFF0F7" : "rgba(255,235,250,0.75)",
                  flex: 1,
                  textAlign: "left",
                  transition: "color 0.15s",
                }}>
                  {item.label}
                </span>

                {/* Planet name */}
                <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.65rem",
                  color: isActive ? item.color : hovered === i ? item.color : "rgba(255,235,250,0.28)",
                  flexShrink: 0,
                  transition: "color 0.15s",
                }}>
                  {item.planet}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Helper: convert hex color to "r, g, b" string for rgba()
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

export default SectionMenu;
